import { Suspense, useEffect, useState } from "react";
import Loading from "../../../common/components/ui/loading/Loading";
import { formatDateAndTime } from "../../../common/helpers/date.helpers";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import { getRentersDelinquency } from "../../../common/services/delinquency.api";
import {
  IRenter,
  RENTER_STATUS,
  FINANCIAL_STATUS,
  RenterType,
} from "../../../types/renter.type";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";

// import { getRenters } from "../../../common/services/renters.api";
import { useNavigate } from "react-router-dom";
import { differenceInDays } from "date-fns";
import ModalChangeStatus from "../../renters/components/modals/ChangeStatusRenter";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { useCompanyStore } from "../../../common/states/store/Company";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { getConsultantOptionsFromAPI } from "@/common/services/employee.api";
import { ConsultantOption } from "@/types/employee.type";

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [RENTER_STATUS.Recall]: "Recolhimento",
  [RENTER_STATUS.WaitingRescision]: "Aguardando recisão",
  [RENTER_STATUS.Blacklist]: "Blacklist",
};

// eslint-disable-next-line react-refresh/only-export-components
export const SITUATION_FINANCIAL_LABELS = {
  [FINANCIAL_STATUS.INADIMPLENTE]: "Inadimplente",
  [FINANCIAL_STATUS.ADIMPLENTE]: "Adimplente",
};
// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabelsRenters(status: RENTER_STATUS) {
  return STATUS_LABELS[status] || status;
}

const EMPTY_ACTION = { eventName: "", data: undefined };

function TableWaitingRescission() {
  const { filterMode, selectedCompanies, selectedUf } = useCompanyStore();

  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [consultantOptions, setConsultantOptions] = useState<
    ConsultantOption[]
  >([]);
  const [renters, setRenters] = useState<IRenter[]>([]);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    situation: "",
    situationFinancial: "",
    filterColumn: "",
    orderBy: "",
  });

  const headers = {
    companyId: "Cidade",
    name: "Nome",
    status: "Status",
    dateRepossesion: "Data do Recolhimento",
    rescisionDaysLeft: "Dias para rescisão",
    updatedAt: "Atualizado em",
    createdAt: "Criado em",
  };

  useEffect(() => {
    const fetchConsultants = async () => {
      let companyFilter: string[] | undefined;
      let ufFilter: string | null = null;

      if (filterMode === "cities" && selectedCompanies.length > 0) {
        companyFilter = selectedCompanies.map((c) => c.companyId);
      } else if (filterMode === "uf") {
        ufFilter = selectedUf;
      }

      const options = await getConsultantOptionsFromAPI(
        companyFilter,
        ufFilter || undefined, // Converte null para undefined
        "PosVenda"
      );
      setConsultantOptions(options);
    };

    fetchConsultants();
  }, [filterMode, selectedCompanies, selectedUf]);

  useEffect(() => {
    async function loadRenters(filters, page) {
      setIsLoading(true);
      setRenters([]);

      const filtersCopy = { ...filters };

      if (filtersCopy.status.length === 0) {
        filtersCopy.status = [
          RENTER_STATUS.Recall,
          RENTER_STATUS.WaitingRescision,
          RENTER_STATUS.Blacklist,
        ].join(",");
      }

      filtersCopy.orderBy = "-updatedAt";
      filtersCopy.situationFinancial = "";

      const result = await getRentersDelinquency({ ...filtersCopy, page });

      const rentersWithRescision = result.data.map((renterData) => {
        let rescisionDaysLeft;
        let dateRepossesion;

        const vehicleHistory = renterData?.desactivationData?.vehicleHistory;
        if (vehicleHistory && vehicleHistory.length > 0) {
          const cancelDate =
            vehicleHistory[vehicleHistory.length - 1].vehicleHistoryEnd;

          rescisionDaysLeft =
            10 - differenceInDays(new Date(), new Date(cancelDate));
          dateRepossesion = cancelDate;
        }

        return { ...renterData, dateRepossesion, rescisionDaysLeft }; // Adiciona a variável ao objeto
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setRenters(rentersWithRescision); // Armazena os dados com a nova propriedade
      setIsLoading(false);
    }

    loadRenters(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, filterMode, selectedCompanies, selectedUf]);

  const actions = [
    {
      label: "Trocar status",
      action: (data) => setAction({ eventName: "changeStatus", data: data }),
      disabled: (data) =>
        [RENTER_STATUS.Ativo].includes(
          (data as RenterType).status as RENTER_STATUS
        ),
      permission: "changeSatus:renters",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Clientes em Recolhimento"
          placeholder="Digite o nome, o tipo ou o e-mail."
          statusOptions={STATUS_LABELS}
          situationOptions={SITUATION_FINANCIAL_LABELS}
          newItemLabel="Novo Colaborador"
          onNewItemClick={() => {}}
          onSearch={handleFilterChange}
          technicalConsultant={consultantOptions}
          entity="delinquency"
          tableData={renters}
        />

        <GenericTable
          headers={headers}
          data={renters}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId);
              },
              name: (name, renter) => (
                <a
                  href={`/aguardandoRecisao/${(renter as IRenter)._id}`}
                  onClick={(e) => {
                    e.preventDefault(); // Para evitar o comportamento padrão do link
                    navigate(`/aguardandoRecisao/${(renter as IRenter)._id}`); // Navegação na mesma página
                  }}
                  target="_blank" // Abre em uma nova aba
                  rel="noopener noreferrer" // Melhoria de segurança
                >
                  {name as string}
                </a>
              ),
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              dateRepossesion: formatDateAndTime,
              status: getStatusLabelsRenters,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="delinquency"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
        {isAction("changeStatus") && (
          <ModalChangeStatus
            renter={action.data as IRenter}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableWaitingRescission;
